/**
 * The control the user just operated, for an intent whose handler does not know which control
 * started it (a report opened from the rail, the chat or the timeline all call the same
 * `openReport`). One passive capturing listener notes the `data-ui` id of each press (pointer,
 * or Enter / Space on a focused control) and the surface around it; `recentOrigin(intent)`
 * returns the id while it is fresh and registered as starting that intent, else null, and
 * `recentSurface(origin)` the surface an intent with that origin was started from.
 */
import { SURFACES } from "./registry";
import { UI_IDS } from "./registry";

/** A press older than this did not start the intent being opened now. */
export const RECENT_CONTROL_MS = 1_500;

let last = null;
/** The last press as `last`, but never consumed: it names the surface of the intent it starts. */
let press = null;
let installed = null;

const monoNow = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

function note(e) {
  if (e.type === "keydown" && e.key !== "Enter" && e.key !== " ") return;
  const el = e.target?.closest?.("[data-ui]");
  const id = el?.getAttribute("data-ui");
  if (!id) return;
  last = { id, at: monoNow() };
  press = { id, surface: surfaceAround(el), at: last.at };
}

/**
 * The registered surface of the innermost `data-surface` region holding `el` (itself
 * included), or null. Pseudo and retired surfaces name no region a control sits in.
 */
export function surfaceAround(el) {
  const name = el?.closest?.("[data-surface]")?.getAttribute("data-surface");
  const entry = name ? SURFACES[name] : undefined;
  return entry && !entry.pseudo && !entry.retired ? name : null;
}

/** Start noting presses on `doc` (idempotent); returns a stop function. */
export function installRecentControl(doc = typeof document !== "undefined" ? document : null) {
  if (!doc || installed === doc) return () => {};
  uninstallRecentControl();
  installed = doc;
  const opts = { capture: true, passive: true };
  doc.addEventListener("pointerdown", note, opts);
  doc.addEventListener("keydown", note, opts);
  return uninstallRecentControl;
}

/** Stop noting presses. */
export function uninstallRecentControl() {
  if (!installed) return;
  const opts = { capture: true };
  installed.removeEventListener("pointerdown", note, opts);
  installed.removeEventListener("keydown", note, opts);
  installed = null;
  last = null;
  press = null;
}

/**
 * Note a keyboard shortcut that is about to start `intent`: `recentOrigin(intent)` then returns
 * `shortcut` (an intent origin) instead of a control's id, for a handler that cannot tell.
 */
export function noteShortcut(intent, { now = monoNow } = {}) {
  last = { id: "shortcut", intent, shortcut: true, at: now() };
  press = null;
}

/**
 * The `data-ui` id pressed in the last `RECENT_CONTROL_MS` when the registry says it starts
 * `intent`, else null. Consumed: one press starts one intent.
 */
export function recentOrigin(intent, { now = monoNow } = {}) {
  if (!last || now() - last.at > RECENT_CONTROL_MS) return null;
  if ((last.shortcut ? last.intent : UI_IDS[last.id]?.intent) !== intent) return null;
  const id = last.id;
  last = null;
  return id;
}

/**
 * The surface an intent with `origin` was started from: the region around the control pressed
 * in the last `RECENT_CONTROL_MS`, when that control is `origin`. Null otherwise — a keyboard
 * shortcut, a chat command, boot or any other path no press of `origin` explains has no
 * surface rather than a guessed one. Not consumed: `recentOrigin` may have taken the press.
 */
export function recentSurface(origin, { now = monoNow } = {}) {
  if (typeof origin !== "string" || !press || now() - press.at > RECENT_CONTROL_MS) return null;
  return press.id === origin ? press.surface : null;
}
